import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuthStore } from "@/store";
import { toast } from "sonner";
import {
  ArrowLeft,
  User,
  Mail,
  Lock,
  Loader2,
  Save,
  Building2,
  MapPin,
  Phone,
  Hash,
} from "lucide-react";
import { useLocalizedNavigation } from "@/hooks/useLocalizedNavigation";
import LocalizedLink from "@/components/LocalizedLink";

export default function AccountPage() {
  const navigate = useLocalizedNavigation();
  const { user, updateProfile, checkAuth, deleteAccount } = useAuthStore();
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [country, setCountry] = useState(user?.country || "");
  const [companyName, setCompanyName] = useState(user?.company_name || "");
  const [vatNumber, setVatNumber] = useState(user?.vat_number || "");
  const [address, setAddress] = useState(user?.address || "");
  const [zipCode, setZipCode] = useState(user?.zip_code || "");
  const [city, setCity] = useState(user?.city || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleSave = async () => {
    if (!name || !email || !country || !companyName || !address || !zipCode || !city || !phone) {
      toast.error("Please fill all required fields. VAT is optional.");
      return;
    }

    setIsSaving(true);
    const result = await updateProfile({
      name,
      country,
      company_name: companyName,
      vat_number: vatNumber,
      address,
      zip_code: zipCode,
      city,
      phone,
    });

    if (result?.success) {
      await checkAuth();
      toast.success("Profile updated successfully");
    } else {
      toast.error(result?.error || "Could not update profile");
    }
    setIsSaving(false);
  };

  const handleDeleteAccount = async () => {
    const confirmed = window.confirm(
      "Are you sure you want to permanently delete your account? This action cannot be undone.",
    );
    if (!confirmed) return;

    setIsDeleting(true);
    const result = await deleteAccount();
    if (result?.success) {
      toast.success("Your account has been deleted.");
      navigate("/");
      return;
    }

    toast.error(result?.error || "Could not delete account.");
    setIsDeleting(false);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="font-semibold">Account Settings</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="grid gap-8">
          {/* Profile Section */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h2 className="text-lg font-semibold mb-6">Profile Information</h2>
            
            <div className="flex items-center gap-6 mb-6">
              <div className="w-20 h-20 rounded-full bg-violet-100 flex items-center justify-center">
                <User className="w-10 h-10 text-violet-600" />
              </div>
              <div>
                <p className="font-semibold text-lg">{user?.name}</p>
                <p className="text-slate-600">{user?.email}</p>
                <p className="text-sm text-slate-500 capitalize mt-1">
                  {user?.role} • {user?.subscription_plan || "Free"} Plan
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="pl-10 h-11"
                    data-testid="account-name"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-11"
                    data-testid="account-email"
                    disabled
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="country">Country</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input id="country" value={country} onChange={(e) => setCountry(e.target.value)} className="pl-10 h-11" />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="company">Company Name</Label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input id="company" value={companyName} onChange={(e) => setCompanyName(e.target.value)} className="pl-10 h-11" />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="vat">VAT (optional)</Label>
                <div className="relative">
                  <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input id="vat" value={vatNumber} onChange={(e) => setVatNumber(e.target.value)} className="pl-10 h-11" />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="address">Address</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input id="address" value={address} onChange={(e) => setAddress(e.target.value)} className="pl-10 h-11" />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="zip">Zip code</Label>
                  <div className="relative">
                    <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input id="zip" value={zipCode} onChange={(e) => setZipCode(e.target.value)} className="pl-10 h-11" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="city">City</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input id="city" value={city} onChange={(e) => setCity(e.target.value)} className="pl-10 h-11" />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} className="pl-10 h-11" />
                </div>
              </div>

              <Button
                onClick={handleSave}
                disabled={isSaving}
                className="btn-primary rounded-full"
                data-testid="save-profile-btn"
              >
                {isSaving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save Changes
              </Button>
            </div>
          </div>

          {/* Security Section */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h2 className="text-lg font-semibold mb-6">Security</h2>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="current-password">Current Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="current-password"
                    type="password"
                    placeholder="••••••••"
                    className="pl-10 h-11"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="new-password">New Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="new-password"
                    type="password"
                    placeholder="••••••••"
                    className="pl-10 h-11"
                  />
                </div>
              </div>

              <Button variant="outline" className="rounded-full">
                Update Password
              </Button>
            </div>
          </div>

          {/* Usage Stats */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h2 className="text-lg font-semibold mb-6">Usage Statistics</h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 bg-slate-50 rounded-xl">
                <p className="text-2xl font-bold text-teal-600">{user?.exports_used || 0}</p>
                <p className="text-sm text-slate-600">Exports Used</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-xl">
                <p className="text-2xl font-bold text-teal-600">{user?.export_limit || 0}</p>
                <p className="text-sm text-slate-600">Export Limit</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-xl">
                <p className="text-2xl font-bold text-teal-600 capitalize">{user?.subscription_plan || "Free"}</p>
                <p className="text-sm text-slate-600">Current Plan</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-xl">
                <p className="text-2xl font-bold text-teal-600 capitalize">{user?.role || "User"}</p>
                <p className="text-sm text-slate-600">Account Type</p>
              </div>
            </div>

            <div className="mt-4">
              <LocalizedLink to="/subscription">
                <Button variant="outline" className="rounded-full">
                  Manage Subscription
                </Button>
              </LocalizedLink>
            </div>
          </div>

          {/* Danger Zone */}
          <div className="bg-white rounded-2xl border border-red-200 p-6">
            <h2 className="text-lg font-semibold text-red-600 mb-4">Danger Zone</h2>
            <p className="text-slate-600 mb-4">
              Once you delete your account, there is no going back. Please be certain and don't forget to cancel your subscription before deleting your account, clicking on the Manage Subscription button.
            </p>
            <Button
              variant="destructive"
              className="rounded-full"
              onClick={handleDeleteAccount}
              disabled={isDeleting}
              data-testid="delete-account-btn"
            >
              Delete Account
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
